import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import UserSettingsService from "src/services/userSettingsService";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { subscriptionPagePath } from "src/ui/navigation/dashboardRouting/dashboardRouting";
import { makeStyles } from "tss-react/mui";

import { useCurrentSubscription, useSubscriptionExpiringWithinMonth } from "@dashboard/workspaces/api/helpers";
import { IconButton, Snackbar, SnackbarProps, Theme } from "@mui/material";
import TimeService from "@services/timeService/timeService";
import { PrimaryButton } from "@shared/CustomButton";

const threeDays = 3;

const useStyles = makeStyles()((theme: Theme) => ({
    orange: {
        "& .MuiSnackbarContent-root": {
            backgroundColor: theme.palette.emteria.warning,
        },
    },
    black: {
        color: theme.palette.emteria.text.primary,
    },
}));

const hasBeenAtLeastThreeDaysSinceLastShown = (lastShownDateMillis: number | null): boolean => {
    if (!lastShownDateMillis) {
        return true;
    }

    const lastShownDate = TimeService.getFromMillis(+lastShownDateMillis);
    const daysSinceLastShow = Math.abs(TimeService.getDaysDifferenceUntilNow(lastShownDate));

    return daysSinceLastShow >= threeDays;
};

export const SubscriptionExpirationBanner = () => {
    const { t } = useTranslation(["common"]);
    const { classes } = useStyles();

    const navigate = useNavigate();
    const { pathname } = useLocation();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const [lastShown, setLastShown] = useState(() => ({
        workspaceId,
        date: UserSettingsService.getWorkspacePageLastExpirationBannerDate(),
    }));

    const lastShownDate = lastShown.date;

    const { data: subscription } = useCurrentSubscription({});
    const daysLeft = subscription?.expirationDate ? TimeService.getDaysDifferenceUntilNow(subscription.expirationDate) : null;

    const isExpiringWithinMonth = useSubscriptionExpiringWithinMonth();
    const open =
        isExpiringWithinMonth && hasBeenAtLeastThreeDaysSinceLastShown(lastShownDate) && pathname !== subscriptionPagePath;

    const hideBanner = () => {
        if (subscription) {
            const nowInMilliseconds = TimeService.getNowInMillis();
            UserSettingsService.setWorkspaceValue("workspacePage.lastExpirationBannerDate", nowInMilliseconds);
            setLastShown({ workspaceId, date: nowInMilliseconds });
        }
    };
    const handleClose: SnackbarProps["onClose"] = (_, reason) => {
        if (reason !== "clickaway") {
            hideBanner();
        }
    };
    const onDetailsClick = () => {
        void navigate(subscriptionPagePath);
    };
    const getMessage = () => {
        if (daysLeft === null) {
            return null;
        }
        const days = Math.abs(daysLeft);
        return t("common:section.expirationBanner.message.expiring", {
            days: days === 0 ? 1 : days,
        });
    };

    if (lastShown.workspaceId !== workspaceId) {
        setLastShown({ workspaceId, date: UserSettingsService.getWorkspacePageLastExpirationBannerDate() });
    }

    return (
        <Snackbar
            anchorOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
            open={open}
            onClose={handleClose}
            className={classes.orange}
            message={<span className={classes.black}>{getMessage()}</span>}
            action={
                <React.Fragment>
                    <PrimaryButton size="small" onClick={onDetailsClick}>
                        {t("common:section.expirationBanner.button.seeDetails")}
                    </PrimaryButton>
                    <IconButton size="small" onClick={hideBanner}>
                        <i className={clsx("fa-solid fa-xmark", classes.black)} />
                    </IconButton>
                </React.Fragment>
            }
        />
    );
};
